/**
 * Runtime configuration: what the SPA needs to know about the origin serving it.
 *
 * One bundle is served by two kinds of origin: a tenant container (the local
 * server in `tenant` mode, one tenant) and the admin app (`full` today,
 * `control_plane` later). Nothing about that can be known at build time, so
 * `index.tsx` fetches `GET /runtime-config` once before rendering and this
 * module holds the answer. Everything else reads it synchronously.
 *
 * A failed fetch (older backend, network) yields the `full` default, which is
 * exactly today's behaviour: same-origin API, probes for the sign-in
 * providers, tenant from the user's roles.
 */
import { BASE_URL } from 'config/api';

export type ServerMode = 'full' | 'control_plane' | 'tenant';

export interface RuntimeConfig {
  mode: ServerMode;
  /** Bound tenant in `tenant` mode; null otherwise. */
  tenant_id: string | null;
  /** Public origin of the admin app (control plane); null when unknown. */
  admin_url: string | null;
  auth: {
    password_enabled: boolean;
    /** null = unknown: keep probing `/azure-auth` as before. */
    azure_enabled: boolean | null;
    google_enabled: boolean | null;
  };
  chat_enabled: boolean | null;
  version: string | null;
}

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  mode: 'full',
  tenant_id: null,
  admin_url: null,
  auth: { password_enabled: true, azure_enabled: null, google_enabled: null },
  chat_enabled: null,
  version: null,
};

let current: RuntimeConfig = DEFAULT_RUNTIME_CONFIG;
let loaded = false;

function normalize(raw: unknown): RuntimeConfig {
  const r = (raw ?? {}) as Partial<RuntimeConfig> & { auth?: Partial<RuntimeConfig['auth']> };
  const mode: ServerMode =
    r.mode === 'tenant' || r.mode === 'control_plane' || r.mode === 'full' ? r.mode : 'full';
  const flag = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
  return {
    mode,
    tenant_id: typeof r.tenant_id === 'string' && r.tenant_id ? r.tenant_id : null,
    admin_url: typeof r.admin_url === 'string' && r.admin_url ? r.admin_url.replace(/\/+$/, '') : null,
    auth: {
      password_enabled: r.auth?.password_enabled !== false,
      azure_enabled: flag(r.auth?.azure_enabled),
      google_enabled: flag(r.auth?.google_enabled),
    },
    chat_enabled: flag(r.chat_enabled),
    version: typeof r.version === 'string' && r.version ? r.version : null,
  };
}

/** Fetch `/runtime-config` once. Never rejects; a failure keeps the default. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const resp = await fetch(`${BASE_URL}/runtime-config`, { headers: { accept: 'application/json' }, cache: 'no-store' });
    if (resp.ok) {
      current = normalize(await resp.json());
    } else {
      console.warn(`⚠️ [RUNTIME] /runtime-config answered ${resp.status}; assuming full mode`);
      current = DEFAULT_RUNTIME_CONFIG;
    }
  } catch (error) {
    console.warn('⚠️ [RUNTIME] /runtime-config unreachable; assuming full mode', error);
    current = DEFAULT_RUNTIME_CONFIG;
  }
  loaded = true;
  return current;
}

/** The loaded configuration (the default until `loadRuntimeConfig` resolves). */
export function getRuntimeConfig(): RuntimeConfig {
  return current;
}

export function isRuntimeConfigLoaded(): boolean {
  return loaded;
}

/** Convenience predicates. */
export const isTenantMode = (): boolean => current.mode === 'tenant';
export const isControlPlaneMode = (): boolean => current.mode === 'control_plane';

/** Tests only: install a configuration without fetching. */
export function setRuntimeConfigForTests(config: Partial<RuntimeConfig> | null): void {
  current = config ? normalize({ ...DEFAULT_RUNTIME_CONFIG, ...config, auth: { ...DEFAULT_RUNTIME_CONFIG.auth, ...(config.auth ?? {}) } }) : DEFAULT_RUNTIME_CONFIG;
  loaded = config !== null;
}

/** React-friendly accessor; the value never changes after boot, so no context is needed. */
export function useRuntimeConfig(): RuntimeConfig {
  return current;
}
